import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useRecoilState } from 'recoil'
import DefaultLayout from '../components/layouts/DefaultLayout'
import {
  Box,
  Text,
  Container,
  Heading,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Spinner,
  IconButton,
  useToast,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Divider,
  Checkbox,
  Badge,
  useBreakpointValue,
  Flex,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from '@chakra-ui/react'
import { FiEdit, FiTrash2, FiArrowLeft, FiMessageSquare } from 'react-icons/fi'
import { authState } from '../atoms/auth'
import {
  getQnaDetail,
  updateQna,
  deleteQna,
  createQnaAnswer,
  updateQnaAnswer,
  deleteQnaAnswer,
} from '../services/apiService'
import dayjs from 'dayjs'

interface QnADetail {
  id: number
  userId: number
  username: string
  title: string
  question: string
  answer: string | null
  answeredBy: number | null
  answeredByUsername: string | null
  answeredAt: string | null
  status: string
  isPrivate: number
  createdAt: string
  updatedAt: string | null
  isMine: boolean
}

const QnADetail: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [auth] = useRecoilState(authState)
  const toast = useToast()
  const { isOpen, onOpen, onClose } = useDisclosure()
  const answerModal = useDisclosure()
  const deleteDialog = useDisclosure()
  const answerDeleteDialog = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, md: false })
  const cancelRef = React.useRef<HTMLButtonElement>(null)
  const answerCancelRef = React.useRef<HTMLButtonElement>(null)

  const [qna, setQna] = useState<QnADetail | null>(null)
  const [loading, setLoading] = useState(false)
  const [isEditMode, setIsEditMode] = useState(false)
  const [formTitle, setFormTitle] = useState('')
  const [formQuestion, setFormQuestion] = useState('')
  const [formIsPrivate, setFormIsPrivate] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [answerContent, setAnswerContent] = useState('')
  const [isAnswerEditMode, setIsAnswerEditMode] = useState(false)

  const fetchQna = async () => {
    if (!id) return
    setLoading(true)
    try {
      const { data: res } = await getQnaDetail(parseInt(id))
      if (res.statusCode === 200) {
        setQna(res.data)
        setFormTitle(res.data.title)
        setFormQuestion(res.data.question)
        setFormIsPrivate(res.data.isPrivate === 1)
        setAnswerContent(res.data.answer || '')
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('질문을 불러오는데 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      navigate('/qna')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchQna()
  }, [id])

  const handleEdit = () => {
    if (!qna || qna.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('수정 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (qna.status === 'ANSWERED') {
      toast({
        title: t('수정 불가'),
        description: t('답변이 완료된 질문은 수정할 수 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    setIsEditMode(true)
    onOpen()
  }

  const handleDeleteClick = () => {
    if (!qna || qna.userId !== auth.id) {
      toast({
        title: t('권한 없음'),
        description: t('삭제 권한이 없습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
      return
    }
    deleteDialog.onOpen()
  }

  const handleDeleteConfirm = async () => {
    if (!qna) return

    try {
      const { data: res } = await deleteQna(qna.id)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        deleteDialog.onClose()
        navigate('/qna')
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleSubmit = async () => {
    if (!formTitle.trim() || !formQuestion.trim()) {
      toast({
        title: t('입력 오류'),
        description: t('제목과 질문 내용을 모두 입력해주세요.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    if (!qna) return

    setSubmitting(true)
    try {
      const { data: res } = await updateQna(qna.id, formTitle, formQuestion, formIsPrivate)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        onClose()
        fetchQna()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('수정에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    } finally {
      setSubmitting(false)
    }
  }

  const handleAnswerSubmit = async () => {
    if (!answerContent.trim() || !qna) {
      toast({
        title: t('입력 오류'),
        description: t('답변 내용을 입력해주세요.'),
        status: 'warning',
        duration: 3000,
        isClosable: true,
      })
      return
    }

    try {
      if (isAnswerEditMode) {
        const { data: res } = await updateQnaAnswer(qna.id, answerContent)
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          answerModal.onClose()
          fetchQna()
        }
      } else {
        const { data: res } = await createQnaAnswer(qna.id, answerContent)
        if (res.statusCode === 200) {
          toast({
            title: t('성공'),
            description: res.message,
            status: 'success',
            duration: 3000,
            isClosable: true,
          })
          answerModal.onClose()
          fetchQna()
        }
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('답변 작성에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  const handleAnswerEdit = () => {
    if (!qna || !qna.answer) return
    setIsAnswerEditMode(true)
    setAnswerContent(qna.answer)
    answerModal.onOpen()
  }

  const handleAnswerDeleteClick = () => {
    if (!qna) return
    answerDeleteDialog.onOpen()
  }

  const handleAnswerDeleteConfirm = async () => {
    if (!qna) return

    try {
      const { data: res } = await deleteQnaAnswer(qna.id)
      if (res.statusCode === 200) {
        toast({
          title: t('성공'),
          description: res.message,
          status: 'success',
          duration: 3000,
          isClosable: true,
        })
        answerDeleteDialog.onClose()
        fetchQna()
      }
    } catch (error: any) {
      toast({
        title: t('오류'),
        description: error.response?.data?.message || t('답변 삭제에 실패했습니다.'),
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  if (loading) {
    return (
      <DefaultLayout>
        <Container maxW="container.xl" py={10}>
          <Box textAlign="center" py={10}>
            <Spinner size="xl" />
          </Box>
        </Container>
      </DefaultLayout>
    )
  }

  if (!qna) {
    return null
  }

  const isAdmin = auth.isAdmin === 1
  const canAnswer = isAdmin && qna.status === 'PENDING'
  const canEditAnswer = isAdmin && qna.status === 'ANSWERED' && qna.answeredBy === auth.id

  return (
    <DefaultLayout>
      <Container maxW="container.xl" py={10}>
        <VStack spacing={6} align="stretch">
          {/* 제목 - 맨 위 */}
          <HStack spacing={2}>
            <Heading size={isMobile ? 'lg' : 'xl'} flex={1}>{qna.title}</Heading>
            {qna.isPrivate === 1 && (
              <Badge colorScheme="purple">{t('비밀글')}</Badge>
            )}
            {qna.status === 'ANSWERED' ? (
              <Badge colorScheme="green">{t('답변완료')}</Badge>
            ) : (
              <Badge colorScheme="gray">{t('대기중')}</Badge>
            )}
          </HStack>

          {/* 뒤로가기 및 수정/삭제 버튼 */}
          <Flex
            direction={isMobile ? 'column' : 'row'}
            gap={isMobile ? 3 : 0}
            align={isMobile ? 'stretch' : 'center'}
            justify="space-between"
          >
            <HStack spacing={2}>
              <IconButton
                aria-label={t('뒤로가기')}
                icon={<FiArrowLeft />}
                onClick={() => navigate('/qna')}
                size={isMobile ? 'md' : 'sm'}
              />
              {isMobile && (
                <Text fontSize="sm" color="gray.500">
                  {t('Q&A')}
                </Text>
              )}
            </HStack>
            {qna.isMine && qna.status === 'PENDING' && (
              <HStack spacing={2} justify={isMobile ? 'flex-end' : 'flex-start'}>
                <Button
                  leftIcon={<FiEdit />}
                  size={isMobile ? 'md' : 'sm'}
                  onClick={handleEdit}
                  flex={isMobile ? 1 : 'none'}
                >
                  {t('수정')}
                </Button>
                <Button
                  leftIcon={<FiTrash2 />}
                  size={isMobile ? 'md' : 'sm'}
                  colorScheme="red"
                  variant="outline"
                  onClick={handleDeleteClick}
                  flex={isMobile ? 1 : 'none'}
                >
                  {t('삭제')}
                </Button>
              </HStack>
            )}
          </Flex>

          {/* 질문 카드 */}
          <Card>
            <CardBody>
              <VStack align="stretch" spacing={4}>
                <HStack justify="space-between" fontSize="sm" color="gray.500">
                  <Text>{qna.username}</Text>
                  <Text>{dayjs(qna.createdAt).format('YYYY-MM-DD HH:mm')}</Text>
                </HStack>
                <Divider />
                <Text whiteSpace="pre-wrap" fontSize="md">{qna.question}</Text>
              </VStack>
            </CardBody>
          </Card>

          {/* 답변 카드 */}
          {qna.answer ? (
            <Card borderLeft="4px solid" borderColor="green.500">
              <CardBody>
                <VStack align="stretch" spacing={4}>
                  <HStack justify="space-between">
                    <HStack spacing={2}>
                      <FiMessageSquare color="green" />
                      <Text fontWeight="bold" color="green.700">
                        {t('답변')}
                      </Text>
                      {qna.answeredByUsername && (
                        <Text fontSize="sm" color="gray.500">
                          {t('by')} {qna.answeredByUsername}
                        </Text>
                      )}
                    </HStack>
                    {canEditAnswer && (
                      <HStack spacing={2}>
                        <IconButton
                          aria-label={t('답변 수정')}
                          icon={<FiEdit />}
                          size="sm"
                          variant="ghost"
                          onClick={handleAnswerEdit}
                        />
                        <IconButton
                          aria-label={t('답변 삭제')}
                          icon={<FiTrash2 />}
                          size="sm"
                          variant="ghost"
                          colorScheme="red"
                                    onClick={handleAnswerDeleteClick}
                        />
                      </HStack>
                    )}
                  </HStack>
                  {qna.answeredAt && (
                    <Text fontSize="xs" color="gray.500">
                      {dayjs(qna.answeredAt).format('YYYY-MM-DD HH:mm')}
                    </Text>
                  )}
                  <Divider />
                  <Text whiteSpace="pre-wrap">{qna.answer}</Text>
                </VStack>
              </CardBody>
            </Card>
          ) : (
            canAnswer && (
              <Card borderLeft="4px solid" borderColor="gray.300">
                <CardBody>
                  <VStack align="stretch" spacing={4}>
                    <Text color="gray.500">{t('아직 답변이 없습니다.')}</Text>
                    <Button
                      leftIcon={<FiMessageSquare />}
                      colorScheme="brand"
                      onClick={() => {
                        setIsAnswerEditMode(false)
                        setAnswerContent('')
                        answerModal.onOpen()
                      }}
                    >
                      {t('답변 작성')}
                    </Button>
                  </VStack>
                </CardBody>
              </Card>
            )
          )}
        </VStack>
      </Container>

      {/* 질문 수정 모달 */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('질문 수정')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('제목')}</FormLabel>
                <Input
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  placeholder={t('질문 제목을 입력하세요')}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('질문 내용')}</FormLabel>
                <Textarea
                  value={formQuestion}
                  onChange={(e) => setFormQuestion(e.target.value)}
                  placeholder={t('질문 내용을 입력하세요')}
                  rows={10}
                />
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={formIsPrivate}
                  onChange={(e) => setFormIsPrivate(e.target.checked)}
                >
                  {t('비밀글')}
                </Checkbox>
                <Text fontSize="sm" color="gray.500" mt={1}>
                  {t('비밀글로 설정하면 작성자와 관리자만 볼 수 있습니다.')}
                </Text>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              {t('취소')}
            </Button>
            <Button
              colorScheme="brand"
              onClick={handleSubmit}
              isLoading={submitting}
            >
              {t('수정')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 답변 작성/수정 모달 */}
      <Modal isOpen={answerModal.isOpen} onClose={answerModal.onClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {isAnswerEditMode ? t('답변 수정') : t('답변 작성')}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel>{t('답변 내용')}</FormLabel>
              <Textarea
                value={answerContent}
                onChange={(e) => setAnswerContent(e.target.value)}
                placeholder={t('답변 내용을 입력하세요')}
                rows={10}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={answerModal.onClose}>
              {t('취소')}
            </Button>
            <Button colorScheme="brand" onClick={handleAnswerSubmit}>
              {isAnswerEditMode ? t('수정') : t('작성')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 질문 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={deleteDialog.isOpen}
        leastDestructiveRef={cancelRef}
        onClose={deleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('정말 삭제하시겠습니까?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={deleteDialog.onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* 답변 삭제 확인 다이얼로그 */}
      <AlertDialog
        isOpen={answerDeleteDialog.isOpen}
        leastDestructiveRef={answerCancelRef}
        onClose={answerDeleteDialog.onClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {t('삭제 확인')}
            </AlertDialogHeader>

            <AlertDialogBody>
              {t('정말 답변을 삭제하시겠습니까?')}
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={answerCancelRef} onClick={answerDeleteDialog.onClose}>
                {t('취소')}
              </Button>
              <Button colorScheme="red" onClick={handleAnswerDeleteConfirm} ml={3}>
                {t('삭제')}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </DefaultLayout>
  )
}

export default QnADetail

